/**
 * Entries table for the Reconcile page. Renders one row per tagged
 * reconcile item with checkbox + date + description + tag pill + account
 * + amount + per-row action button (Reconcile / Confirm amount /
 * Unreconcile / —).
 *
 * Empty state ("Nothing here for <month>") owned here so the shell only
 * branches on whether the table or the empty placeholder is rendered.
 *
 * SortableTh is a private atom — only this view sorts.
 */
import React from 'react';
import { motion } from 'framer-motion';
import {
  ArrowUp, ArrowDown, ArrowUpDown, CheckCircle2, Receipt, Repeat, Sparkles, Undo2,
} from 'lucide-react';
import { Checkbox } from '../../components/ui/checkbox';
import { FADE_IN_HIDDEN, FADE_IN_SHOWN } from '../../lib/animations';
import { fmtMoney } from '../../lib/format';
import { toUIType } from '../../lib/liabilityType';
import { TAG_STYLES, TAG_LABELS } from '../../lib/reconcileTags';
import { MONTHS } from './constants';

const SortableTh = ({ column, label, sortConfig, onSort, align = 'left' }) => {
  const active = sortConfig.column === column;
  const Icon = active ? (sortConfig.direction === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;
  const thAlign = align === 'right' ? 'text-right' : 'text-left';
  return (
    <th className={`${thAlign} px-6 py-4`}>
      <button
        type="button"
        onClick={() => onSort(column)}
        className={`inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] font-semibold transition-colors ${
          active ? 'text-[#0052FF]' : 'text-[#64748B] hover:text-[#020617]'
        }`}
        data-testid={`sort-${column}`}
        aria-label={`Sort by ${label}`}
      >
        {label}
        <Icon className={`w-3 h-3 ${active ? 'opacity-100' : 'opacity-40'}`} />
      </button>
    </th>
  );
};

const ReconcileTable = ({
  rows, year, month, sortConfig, toggleSort,
  selectedKeys, bulkEligibleKeys, toggleSelect, toggleSelectAll,
  accountNameMap, accountKindMap,
  busy, handleRowAction,
}) => {
  if (rows.length === 0) {
    return (
      <div className="bg-white border border-[#E2E8F0] rounded-lg p-12 text-center" data-testid="empty-state">
        <p className="text-[#64748B]">Nothing here for {MONTHS[month - 1].label} {year}.</p>
      </div>
    );
  }

  return (
    <motion.div initial={FADE_IN_HIDDEN} animate={FADE_IN_SHOWN} className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-[#F8FAFC] border-b border-[#E2E8F0]">
            <tr>
              <th className="w-12 px-4 py-4 print-hide">
                <Checkbox
                  checked={bulkEligibleKeys.length > 0 && selectedKeys.size === bulkEligibleKeys.length}
                  onCheckedChange={toggleSelectAll}
                  disabled={bulkEligibleKeys.length === 0}
                  data-testid="select-all-checkbox"
                  aria-label="Select all eligible items"
                />
              </th>
              <SortableTh column="date" label="Date" sortConfig={sortConfig} onSort={toggleSort} />
              <SortableTh column="description" label="Description" sortConfig={sortConfig} onSort={toggleSort} />
              <SortableTh column="tag" label="Tag" sortConfig={sortConfig} onSort={toggleSort} />
              <SortableTh column="account" label="Account" sortConfig={sortConfig} onSort={toggleSort} />
              <SortableTh column="amount" label="Amount" sortConfig={sortConfig} onSort={toggleSort} align="right" />
              <th className="text-right px-6 py-4 text-xs uppercase tracking-[0.2em] font-semibold text-[#64748B] print-hide">Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const isEligible = !row.is_estimated && !row.reconciled;
              const Icon = row.kind === 'projection' ? Sparkles : Receipt;
              return (
                <tr key={row.key} className="border-b border-[#E2E8F0] last:border-0 hover:bg-[#F8FAFC] transition-colors" data-testid={`reconcile-row-${row.key}`}>
                  <td className="px-4 py-4 print-hide">
                    <Checkbox
                      checked={selectedKeys.has(row.key)}
                      onCheckedChange={() => isEligible && toggleSelect(row.key)}
                      disabled={!isEligible}
                      data-testid={`row-select-${row.key}`}
                      aria-label="Select for bulk reconcile"
                    />
                  </td>
                  <td className="px-6 py-4 text-sm text-[#334155] whitespace-nowrap">
                    {new Date(row.date).toLocaleDateString('en-GB')}
                  </td>
                  <td className="px-6 py-4 text-sm">
                    <span className="inline-flex items-center gap-1.5 flex-wrap">
                      <Icon className={`w-3.5 h-3.5 ${row.kind === 'projection' ? 'text-[#1E40AF]' : 'text-[#64748B]'}`} />
                      <span className="font-medium text-[#020617]">{row.description}</span>
                      {row.transfer_id && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider bg-[#E0E7FF] text-[#3730A3]" title="Transfer between accounts">
                          Transfer
                        </span>
                      )}
                      {row.is_estimated && (
                        <span className="inline-flex items-center justify-center h-5 px-2 rounded text-[10px] font-semibold uppercase tracking-wider bg-[#FEF3C7] text-[#92400E] leading-none align-middle" title="Estimated amount">
                          EST
                        </span>
                      )}
                      {row.kind === 'projection' && (
                        <span className="text-xs text-[#64748B] inline-flex items-center gap-1">
                          <Repeat className="w-3 h-3" />
                          from recurring
                        </span>
                      )}
                      {row.category && (
                        <span className="text-xs text-[#64748B]">· {row.category}</span>
                      )}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center justify-center h-6 leading-none px-2.5 rounded-full text-[11px] font-medium ${TAG_STYLES[row.tag]}`} data-testid={`tag-${row.tag}-${row.key}`}>
                      {TAG_LABELS[row.tag]}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-[#334155]">
                    {accountNameMap[row.account_id] || '-'}
                  </td>
                  <td className={`px-6 py-4 text-sm text-right font-semibold ${toUIType(row.transaction_type, accountKindMap[row.account_id]) === 'income' ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>
                    {toUIType(row.transaction_type, accountKindMap[row.account_id]) === 'income' ? '+' : '-'}£{fmtMoney(row.amount)}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="print-hide inline-block">
                      {row.reconciled ? (
                        row.kind === 'transaction' ? (
                          <button
                            type="button"
                            onClick={() => handleRowAction(row)}
                            disabled={busy}
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors bg-[#F1F5F9] text-[#475569] hover:bg-[#E2E8F0] ${busy ? 'opacity-60 cursor-not-allowed' : ''}`}
                            data-testid={`row-action-${row.key}`}
                            aria-label="Undo reconciliation"
                          >
                            <Undo2 className="w-3.5 h-3.5" />
                            Unreconcile
                          </button>
                        ) : (
                          <span className="text-xs text-[#64748B]">—</span>
                        )
                      ) : (
                        <button
                          type="button"
                          onClick={() => handleRowAction(row)}
                          disabled={busy}
                          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                            row.is_estimated
                              ? 'bg-[#FEF3C7] text-[#92400E] hover:bg-[#FDE68A]'
                              : 'bg-[#FFEDD5] text-[#9A3412] hover:bg-[#FED7AA]'
                          } ${busy ? 'opacity-60 cursor-not-allowed' : ''}`}
                          data-testid={`row-action-${row.key}`}
                          aria-label={row.is_estimated ? 'Confirm amount and reconcile' : 'Reconcile this item'}
                        >
                          <CheckCircle2 className="w-3.5 h-3.5" />
                          {row.is_estimated ? 'Confirm amount' : 'Reconcile'}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </motion.div>
  );
};

export default ReconcileTable;
